import { omit, get } from 'lodash';
import { Col } from 'antd';
import React, { useCallback } from 'react';

import Panel, { PanelProps } from '.';

import countries from '../../../resources/data/countries.json';
import states from '../../../resources/data/states.json';
import FormItem from '../form-item';
import { FormElementChangeEvent, OptionProps } from '../index';
import { ADDRESS_SCHEMA_TEMPLATE } from '../../../utils/form-builder';

export enum FieldKind {
  AddressLine1,
  AddressLine2,
  ZipCode,
  City,
  USState,
  NonUSState,
  Country,
}

const DEFAULT_COUNTRY_VALUE = 'US';

export function getLayoutProps(index: number) {
  let xs;
  let md;
  switch (index) {
    case FieldKind.AddressLine1:
      xs = { span: 24, order: 1 };
      md = { span: 9, order: index + 1 };
      break;
    case FieldKind.AddressLine2:
      xs = { span: 24, order: 2 };
      md = { span: 9, order: index + 1 };
      break;
    case FieldKind.ZipCode:
      xs = { span: 12, order: 4 };
      md = { span: 6, order: index + 1 };
      break;
    case FieldKind.City:
      xs = { span: 12, order: 3 };
      md = { span: 9, order: index + 1 };
      break;
    case FieldKind.NonUSState:
    case FieldKind.USState:
      xs = { span: 12, order: 5 };
      md = { span: 9, order: index + 1 };
      break;
    case FieldKind.Country:
      xs = { span: 12, order: 6 };
      md = { span: 6, order: index + 1 };
      break;
  }

  return { xs, md };
}

function getFieldNameByFieldKind(kind: FieldKind) {
  return get(ADDRESS_SCHEMA_TEMPLATE, `elements[${kind}].name`);
}

const PanelAddress = React.memo(function PanelAddress({
  elements,
  value: panelValue,
  disabled,
  repeatable,
  ...props
}: PanelProps) {
  const fieldRenderer = useCallback(
    (_: string, attributes: any, error: any, Comp: React.ComponentType<any>, index = 0) => {
      const { name, label, itemIndex } = attributes;
      const componentProps = omit(attributes, ['instructiveLabel', 'itemIndex']);
      componentProps.disabled = disabled;
      const isCountry = index === FieldKind.Country;
      const isUsState = index === FieldKind.USState;
      const isNonUsState = index === FieldKind.NonUSState;

      if (isCountry || isUsState) {
        componentProps.showSearch = true;
        componentProps.filterOption = (input: string, option: React.ReactElement<OptionProps>) =>
          option &&
          option.props !== null &&
          option.props.children !== null &&
          typeof option.props.children === 'string' &&
          option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0;
        componentProps.options = isCountry
          ? Object.keys(countries as any).map((key: string) => {
              return { label: countries[key as keyof typeof countries], value: key };
            })
          : states.map((state: { name: string; abbreviation: string }) => {
              return { label: state.name, value: state.abbreviation };
            });
      }

      const countryFieldName = getFieldNameByFieldKind(FieldKind.Country);
      const countryValue = get(panelValue, repeatable ? `[${itemIndex}].${countryFieldName}` : countryFieldName);
      // hide US State dropdown
      if (isUsState && countryValue !== DEFAULT_COUNTRY_VALUE) {
        return null;
      }
      // hide Non-US State text
      if (isNonUsState && countryValue === DEFAULT_COUNTRY_VALUE) {
        return null;
      }

      if (isCountry) {
        const { value, onChange } = componentProps;
        // set default country value when the value is not set
        if (value === undefined) {
          delete componentProps.value;
          componentProps.defaultValue = DEFAULT_COUNTRY_VALUE;
          if (onChange) {
            onChange({ name, value: componentProps.defaultValue });
          }
        }
        componentProps.onChange = ({ name, value }: FormElementChangeEvent) => {
          // reset `state` value when `country` changes
          onChange({ name: elements[FieldKind.USState].name, value: undefined });
          // call the original `onChange`
          onChange({ name, value });
        };
      }

      // responsive layout config
      const layoutProps = getLayoutProps(index);

      return (
        <Col {...layoutProps}>
          <FormItem label={label} htmlFor={componentProps.id} error={error}>
            <Comp error={error} {...componentProps} />
          </FormItem>
        </Col>
      );
    },
    [disabled, elements, panelValue, repeatable]
  );

  return (
    <Panel
      {...props}
      elements={elements}
      fieldRenderer={fieldRenderer}
      value={panelValue}
      disabled={disabled}
      repeatable={repeatable}
    />
  );
});

export default PanelAddress;
